import React, { useState, useEffect } from "react";
import axios from "axios";
import Cookies from "js-cookie";
import Swal from "sweetalert2";
import CompanyNavbar from "../company/CompanyNavbar";
import url from "../../function/url";
import "../company/formstyle.css";

function AddRating() {
    const ID = Cookies.get("log_id"); // Logged-in user ID from cookies
    const [formData, setFormData] = useState({
        employee: "",
        rating_factor: "",
        value: "",
    });
    const [employees, setEmployees] = useState([]); // List of employees
    const [ratingFactors, setRatingFactors] = useState([]); // List of rating factors
    const [loading, setLoading] = useState(true); // Loading state
    const [error, setError] = useState({
        employee: "",
        rating_factor: "",
        value: "",
    });

    // Fetch employees and rating factors on component load
    useEffect(() => {
        const fetchData = async () => {
            try {
                if (!ID) throw new Error("User not logged in. Please log in again.");
    
                setLoading(true); // Start loading
    
                // Fetch employees and rating factors in parallel
                const [employeeRes, factorRes] = await Promise.all([
                    axios.get(`${url.path}/employees/`, {
                        params: { log_id: ID },
                        headers: { "X-CSRFToken": Cookies.get("csrftoken") },
                    }).catch(error => {
                        if (error.response && error.response.status === 404) {
                            console.log("No employees found");
                            return { data: [], noEmployees: true }; // Handle 404 case
                        }
                        throw error; // Other errors should be handled normally
                    }),
                    axios.get(`${url.path}/ratingfactors/${ID}/`),
                ]);
    
                if (employeeRes.noEmployees) {
                    setEmployees([]); // Ensure employees list is empty
                    setError("No employees found for this company.");
                } else {
                    setEmployees(employeeRes.data); // Set employees list
                }
    
                setRatingFactors(factorRes.data); // Set rating factors list
            } catch (err) {
                console.error("Error fetching data:", err);
    
                if (err.response) {
                    setError(err.response.data.message || "Failed to fetch data. Please try again.");
                } else {
                    setError("Failed to fetch data. Please check your connection.");
                }
            } finally {
                setLoading(false); // Turn off loading
            }
        };
    
        fetchData();
    }, [ID]);
    

    // Handle input changes
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
        setError({ ...error, [name]: "" }); // Clear individual field errors when user types
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();
        let newErrors = {};

        if (!formData.employee) {
            newErrors.employee = "Please select an employee.";
        }

        if (!formData.rating_factor) {
            newErrors.rating_factor = "Please select a rating factor.";
        }

        const value = parseFloat(formData.value);
        if (isNaN(value) || value < 0 || value > 5) {
            newErrors.value = "Rating value must be between 0 and 5.";
        }

        // If any validation error exists, set error state and stop submission
        if (Object.keys(newErrors).length > 0) {
            setError(newErrors);
            return;
        }

        try {
            if (!ID) throw new Error("User not logged in. Please log in first.");

            const payload = {
                employee: formData.employee,
                rating_factor: formData.rating_factor,
                value: formData.value,
                login: ID,
            };
            console.log("Payload being sent:", payload);

            // API call to add a rating
            await axios.post(`${url.path}/ratingadd/`, payload);

            Swal.fire({
                icon: "success",
                title: "Rating added successfully!",
                timer: 2000,
            });

            // Reset form data
            setFormData({
                employee: "",
                rating_factor: "",
                value: "",
            });
            setError({ employee: "", rating_factor: "", value: "" }); // Clear errors on success
        } catch (err) {
            Swal.fire({
                icon: "error",
                title: "Error adding rating.",
                text: err.message || "Please try again.",
            });
        }
    };

    if (loading) return <p>Loading...</p>;

    return (
        <>
            <CompanyNavbar /> {/* Navigation bar */}
            <div className="page-wrapper">
                <div className="form-container">
                    <h1 className="text-2xl font-bold mb-4">Add Rating</h1>
                    {error.general && <p className="text-red-500">{error.general}</p>}

                    <form onSubmit={handleSubmit} className="space-y-6">
                        {/* Employee Selection */}
                        <div>
                            <label className="block font-medium mb-1">Employee</label>
                            <select
                                name="employee"
                                value={formData.employee}
                                onChange={handleChange}
                                className="w-full border rounded p-2"
                            >
                                <option value="">Select an Employee</option>
                                {employees.map((emp) => (
                                    <option key={emp.id} value={emp.id}>
                                        {emp.Employee_Name}
                                    </option>
                                ))}
                            </select>
                            {error.employee && <p className="text-red-500 text-sm mt-1">{error.employee}</p>}
                        </div>

                        {/* Rating Factor Selection */}
                        <div>
                            <label className="block font-medium mb-1">Rating Factor</label>
                            <select
                                name="rating_factor"
                                value={formData.rating_factor}
                                onChange={handleChange}
                                className="w-full border rounded p-2"
                            >
                                <option value="">Select a Rating Factor</option>
                                {ratingFactors.map((factor) => (
                                    <option key={factor.id} value={factor.id}>
                                        {factor.rating_factor}
                                    </option>
                                ))}
                            </select>
                            {error.rating_factor && <p className="text-red-500 text-sm mt-1">{error.rating_factor}</p>}
                        </div>

                        {/* Rating Value Input */}
                        <div>
                            <label className="block font-medium mb-1">Value</label>
                            <input
                                type="number"
                                name="value"
                                value={formData.value}
                                onChange={handleChange}
                                className="w-full border rounded p-2"
                                step="0.01"
                            />
                            {error.value && <p className="text-red-500 text-sm mt-1">{error.value}</p>}
                        </div>

                        <button
                            type="submit"
                            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                        >
                            Add Rating
                        </button>
                    </form>
                </div>
            </div>
        </>
    );
}

export default AddRating;
